import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GoogleMap, LoadScript, Polyline, Polygon, Marker, InfoWindow, Circle, StandaloneSearchBox } from '@react-google-maps/api';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { useDeepCompareEffect } from 'react-use';
import debounce from 'lodash.debounce'; 
import { computeOffset, computeHeading } from 'spherical-geometry-js';

import { MarkerCircle, MarkerIcon } from './Markers';
import {
  wellColor,
  generateSimpleDirectionalSurvey,
  getSurveysIntersectingCylinder,
  generateFpzPolygon,
  generateFpzPolygonFromPath,
  findHeelPoint,
} from '../../common/functions';
import { gisActions } from '../../actions';
import InfoWindowContent from './InfoWindowContent';
import { GOOGLE_MAPS_API_KEY } from '../../common/constants';

// TEMP
// import tempDirectionalSurveys2 from "../../tempData/crescentpoint10977surveys.json";

const containerStyle = {
  width: '100%',
  height: '100%'
};

const markerGrey = require('../../assets/images/grey.png');
const markerBlue = require('../../assets/images/blue.png');
const markerRed = require('../../assets/images/red.png');

const MIN_WELL_ZOOM = 8;

const Map = ({
  mapHeight = '100%',
  directionalSurveys = [],
  toggleCartofactWells,
  setDirectionalSurveys=() => {},
  cartofactWells,
  selected,
  fpzRadius,
  mapOptions = {
    scrollwheel: true,
    fullscreenControl: false,
  },
  newCenter,
  newZoom,
  defaultFpzRadiusColor = '#66FFFF',
  defaultFpzRadius = 500,
  selectedTreatmentWells = [],
  viewOffsetWells = false,
  zoomOverlay = true,
}) => {
  const dispatch = useDispatch();
  const mapRef = useRef();
  const searchBoxRef = useRef(null);
  const hasFitBounds = useRef(false);

  const fpzState = useSelector((state) => state.fpzReducer);
  const defaultCenter = useMemo(() => {
    return fpzState?.map?.center || {
      lat: 50.5,
      lng: -105.8
    };
  }, [fpzState?.map?.center]);
  
  const defaultZoom = useMemo(() => {
    return fpzState?.map?.zoom || 5;
  }, [fpzState?.map?.zoom]);

  const defaultBoundingBox = useMemo(() => {
    return fpzState?.map?.boundingBox || null;
  }, [fpzState?.map?.boundingBox]);
  

  const [mapContainerStyle, setMapContainerStyle] = useState(containerStyle);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapType, setMapType] = useState('hybrid');
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [currentZoom, setCurrentZoom] = useState(defaultZoom);
  const [renderInfoWindow, setRenderInfoWindow] = useState(4);
  const [flexBounds, setFlexBounds] = useState([]);
  const [fpzSecondaryRadius, setFpzSecondaryRadius] = useState(750);
  const [operators, setOperators] = useState(null);
  const [validCartofactWells, setValidCartofactWells] = useState([]); 
  const [availableDirectionalSurveys, setAvailableDirectionalSurveys] = useState([]);
  const [boundingBox, setBoundingBox] = useState(null);
  
  // fit bounds
  useEffect(() => { 
    if (mapRef.current && flexBounds?.length > 0) {
      console.log('flexBounds', flexBounds);
      const bounds = new window.google.maps.LatLngBounds();
      flexBounds.forEach(obj => {
        bounds.extend(obj);
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [flexBounds]);
  

  // handle change to map height
  useEffect(() => {
    if (mapHeight) {
      setMapContainerStyle((prevStyle) => ({
        ...prevStyle,
        height: mapHeight,
      }));
    }
  }, [mapHeight]);

  // handle change to map center
  useEffect(() => {
    if (mapRef.current && mapCenter) {
      mapRef.current.panTo(mapCenter);
    }
  }, [mapCenter]);

  useEffect(() => {
    if (mapRef.current && newCenter) {
      mapRef.current.panTo(newCenter);
      setTimeout(() => {
        mapRef.current.setZoom(newZoom);
      }, 500);
    }
  }, [newCenter, newZoom]);

  // handle change to map zoom
  useEffect(() => {
    if (mapRef.current && mapZoom) {
      setTimeout(() => {
        mapRef.current.setZoom(mapZoom);
      }, 500);
    }
  }, [mapZoom]);


  useEffect(() => {
    let allPoints = [];
    directionalSurveys?.map((survey, index) => {
      let surveyData = survey.coordinates || [];

      // Sort by depth
      surveyData = [...surveyData].sort((a, b) => a.depth - b.depth);

      // Remove zero-length segments
      surveyData = surveyData?.filter((point, idx, arr) => {
        if (idx === 0) return true;
        const prev = arr[idx - 1];
        return point.lat !== prev.lat || point.lng !== prev.lng;
      });

      // Add all points to allPoints
      surveyData.forEach(point => {
        allPoints.push({ lat: point.lat, lng: point.lng });
      });
      
    });

    if (!hasFitBounds.current) {
      setFlexBounds(allPoints);
      hasFitBounds.current = true;
    }

  }, [directionalSurveys]);

  useEffect(() => {
    const tempDirectionalSurveys = [];
    const validMarkers = cartofactWells?.filter((marker) => {
      // Check if lat and lng are valid
      const isValidLat = typeof marker.lat === 'number' && marker.lat >= -90 && marker.lat <= 90;
      const isValidLng = typeof marker.lng === 'number' && marker.lng >= -180 && marker.lng <= 180;
      if (isValidLat && isValidLng) {
        let bottomHoleLat, bottomHoleLng, survey, polylinePath;
        if (marker.bottom_hole_coordinates) {
          const [latStr, lngStr] = marker.bottom_hole_coordinates.split(',').map(coord => coord.trim());
          bottomHoleLat = parseFloat(latStr);
          bottomHoleLng = parseFloat(lngStr);
          marker.bottomHoleLat = bottomHoleLat;
          marker.bottomHoleLng = bottomHoleLng;

          survey = generateSimpleDirectionalSurvey({
            surfaceLat: marker.lat,
            surfaceLng: marker.lng,
            bottomHoleLat,
            bottomHoleLng,
            totalDepth: marker.tvd,
            heelDepth: marker.tvd, // default pass same value as totalDepth
            well: marker
          });
          tempDirectionalSurveys.push(survey); 
          polylinePath = survey.coordinates;
        }
        marker.polylinePath = marker?.directional_survey || polylinePath;
        marker.markerType = 'cartofactWell';
        return true;
      } else {
        return false;
      }
    });
    setValidCartofactWells(validMarkers);
    setAvailableDirectionalSurveys(tempDirectionalSurveys);
    if (!hasFitBounds.current) {
      setFlexBounds(validMarkers);
      hasFitBounds.current = true;
    }
  }, [cartofactWells]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setMapLoaded(true);

    // mpap type change listener
    map.addListener('maptypeid_changed', () => {
      const newMapTypeId = map.getMapTypeId();
      if (newMapTypeId !== mapType) {
        setMapType(newMapTypeId);
      }
    });

    // zoom changes listener
    map.addListener('zoom_changed', () => {
      const newZoom = map.getZoom();
      if (newZoom !== mapZoom) {
        setCurrentZoom(newZoom);
        //setMapZoom(15);
      }
    });
    
  }, []);

  const onPlacesChanged = () => {
    const places = searchBoxRef.current.getPlaces();
    if (places && places.length > 0) {
      const location = places[0].geometry.location;
      setMapCenter({
        lat: location.lat(),
        lng: location.lng(),
      });
      setMapZoom(20);
    }
  };

  const handleSelectTreatmentWell = async (marker) => {
    const point1 = marker.heel;
    const point2 = marker.toe;
    console.log('handleSelectTreatmentWell', marker);

    if (mapRef.current) {
      mapRef.current.panTo({ lat: point1.lat, lng: point1.lng });
      setTimeout(() => {
        // mapRef.current.setZoom(12);
      }, 500);

      dispatch({ type: 'ADD_TREATMENT_WELL', payload: marker });
    }
  };

  const handleSelectOffsetWell = async (marker) => {
    const point1 = marker.heel;
    const point2 = marker.toe;
    console.log('handleSelectOffsetWell', marker);

    if (mapRef.current) {
      mapRef.current.panTo({ lat: point1.lat, lng: point1.lng });
      setTimeout(() => {
        // mapRef.current.setZoom(12);
      }, 500);

      dispatch({
        type: 'ADD_OFFSET_WELL', payload: {
          well: selectedTreatmentWells[selected],
          offsetWell: { ...marker, verified: true }
        }
      });

      handleCloseInfoWindow();
    }
  };

  const handleDeleteOffsetWell = async (marker) => {
    dispatch({
      type: 'DELETE_OFFSET_WELL', payload: {
        well: selectedTreatmentWells[selected],
        offsetWell: marker
      }
    });
    handleCloseInfoWindow();
  };

  const renderDirectionalSurveys = () => {
    const polylines = directionalSurveys?.map((survey, index) => {
      let surveyData = survey.coordinates || [];

      // Sort by depth
      surveyData = [...surveyData].sort((a, b) => a.depth - b.depth);

      // Remove zero-length segments
      surveyData = surveyData?.filter((point, idx, arr) => {
        if (idx === 0) return true;
        const prev = arr[idx - 1];
        return point.lat !== prev.lat || point.lng !== prev.lng;
      });

      return (
        <Polyline
          key={index}
          path={surveyData?.map(p => ({ lat: p.lat, lng: p.lng }))}
          options={{ strokeColor: '#FF0000', strokeOpacity: 1.0, strokeWeight: 2 }}
          onClick={async () => {
            console.log('Survey clicked:', survey);
            try {
              // find corresponding cartofact well info
              const well = await gisActions.getCartofactWells({ boundingBox, filter: survey.coordinates[0].uwi });
              console.log('well', well);
              if (Array.isArray(well) && well.length > 0) {
                handleOpenInfoWindow({
                  ...well[0],
                  markerType: 'cartofactWell',
                });
              }
            } catch (error) {
              console.error('Error fetching cartofact well:', error);
            }
          }}
        />
      );
    });

    return polylines;
  };

  const renderFpz = (marker) => {
    if (!marker || !marker.heel || !marker.toe) return null;

    // console.log('renderFpz:marker', marker);
    //generateFpzPolygonFromPath(marker, fpzRadius, 16);
    

    const well = selectedTreatmentWells?.find(
      (w) => w.unique_identifier === marker.unique_identifier
    );

    if (!well?.fpzRadiuses?.length) return null;

    let zIndex = 9;
    return well.fpzRadiuses.map((fpzRadius, index) =>
      //renderFpzPolygon(marker, well.heel, well.toe, fpzRadius, index)
      <Polygon
        key={`fpzRadius-${index}`}
        path={marker?.directional_survey ? generateFpzPolygon(marker, fpzRadius.radius, 16).path : generateFpzPolygon(marker, fpzRadius.radius, 16).path}
        zIndex={zIndex - index}
        options={{
          strokeColor: fpzRadius?.color || defaultFpzRadiusColor,
          strokeOpacity: 0.8,
          strokeWeight: 1,
          fillColor: fpzRadius?.color || defaultFpzRadiusColor,
          fillOpacity: 0.35,
        }}
      />
    );
  };

  const renderMarker = (marker, index) => {
    if (!mapRef.current) return null;

    let bottomHoleLat = marker?.bottomHoleLat;
    let bottomHoleLng = marker?.bottomHoleLng;
    let polylinePath = marker?.polylinePath;
    let heel, toe, isTreatment, isOffset;
    if (polylinePath && polylinePath.length > 0) {
      heel = polylinePath[1];
      toe = polylinePath[polylinePath.length - 1];
    }
    // console.log('polylinePath', polylinePath, 'heel', heel, 'toe', toe);
    marker.heel = findHeelPoint(polylinePath) || heel;
    marker.toe = toe;
    isTreatment = selectedTreatmentWells?.find(well => well.unique_identifier === marker.unique_identifier);
    if (viewOffsetWells && 
      selected !== null &&
      selected !== undefined &&
      selectedTreatmentWells[selected]?.unique_identifier !== marker.unique_identifier) {
      isTreatment = false;
    }
    isOffset = marker?.markerType === 'offsetWell';/*selectedTreatmentWells?.some(
      well =>
        well.offsetWells?.some(
          offset => offset.unique_identifier === marker.unique_identifier
        )
    );*/
    // console.log('isOffset', isOffset, 'isTreatment', isTreatment, 'marker', marker);
    let zIndex = 2;
    if (isTreatment) zIndex = 10;
    if (isOffset) zIndex = 8;
    return (
      <React.Fragment key={`comboCartofactWell-${index}`}>
        {/* marker for surface location */} 
        <Marker
          key={`cartofactWell-${index}`}
          position={{
            lat: marker.lat,
            lng: marker.lng,
          }}
          icon={
            {
              url: isTreatment
              ? markerRed
              : isOffset
              ? markerBlue
              : markerGrey,
              scaledSize: new window.google.maps.Size(15, 15),
              anchor: new window.google.maps.Point(7.5, 7.5),
            }
          }
          draggable={false}
          zIndex={zIndex}
          onMouseOver={() => {
            // console.log('Marker hovered:', marker);
            //handleOpenInfoWindow(marker);
          }}
          onClick={(event) => {
            console.log('Marker clicked:', marker);
            handleOpenInfoWindow(marker);
          }}
        />
        {/* Line from Surface to Bottom Hole (if bottomHole coordinates exist) */}
        {
          
          
          (bottomHoleLat
          && bottomHoleLng
          && (currentZoom >= 11
          || isTreatment
          || isOffset
          )) && (
          <Polyline
            path={polylinePath}
            zIndex={zIndex}
            options={{
              strokeColor: '#FF5733', // line color
              strokeOpacity: 0.8,
              strokeWeight: 2,
              geodesic: true,
            }}
            onClick={(event) => {
              console.log('Line clicked:', marker);
              handleOpenInfoWindow(marker);
            }}
          />
        )}
        {/* Line from Surface to Bottom Hole (if bottomHole coordinates exist) */}
        {(bottomHoleLat
          && bottomHoleLng
          && (isTreatment)) && (
          renderFpz(marker)
        )}
      </React.Fragment>
    )

  };

  const renderCartofactWells = () => {
    // cartofactWells, toggleTrajectoryLines
    
    return validCartofactWells?.map((marker, index) => {
      return renderMarker(marker, index);
    });
  };

  const renderSelectedTreatmentWells = () => {
    const treatmentWells = selectedTreatmentWells || [];
    return treatmentWells.map((marker, index) => {
      return renderMarker(marker, index);
    });
  };

  const renderSelectedOffsetWells = () => {
    if (!viewOffsetWells) return null;
    
    /*let offsetWells;
    if (!selected) {
      offsetWells = selectedTreatmentWells?.[0]?.offsetWells || [];
      return offsetWells.map((marker, index) => {
        return renderMarker({ ...marker, markerType: 'offsetWell' }, index);
      });
    }
    offsetWells = selectedTreatmentWells[selected]?.offsetWells || [];
    return offsetWells.map((marker, index) => {
      return renderMarker({ ...marker, markerType: 'offsetWell' }, index);
    });*/

    const markers =
      selected == null
        ? selectedTreatmentWells.flatMap(tw => tw?.offsetWells ?? [])
        : (selectedTreatmentWells[selected]?.offsetWells ?? []);

    return markers.map((marker, i) =>
      renderMarker({ ...marker, markerType: 'offsetWell' }, i)
    );
    
  };
  
  const handleOnIdle = async () => {
    if (mapRef.current) {
      console.log("currentZoom:", currentZoom);
      const center = mapRef.current.getCenter();
      const currentBounds = mapRef.current.getBounds();
      const ne = currentBounds.getNorthEast();
      const sw = currentBounds.getSouthWest();
      const zoom = mapRef.current.getZoom();
      
      // console.log('zoom', zoom);
      if (zoom < MIN_WELL_ZOOM) return;

      const boundingBox = {
        lat_min: sw.lat(),
        lng_min: sw.lng(),
        lat_max: ne.lat(),
        lng_max: ne.lng(),
      };

      // console.log('onIdle: boundingBox', boundingBox, 'mapCenter', mapRef.current.getCenter().toJSON(), 'mapZoom', mapRef.current.getZoom());

      dispatch({
        type: 'SET_MAP',
        payload: {
          center: { lat: center.lat(), lng: center.lng() },
          zoom: zoom,
          boundingBox
        }
      });

    }
  };

  const handleOpenInfoWindow = (data) => {
    console.log('handleOpenInfoWindow', { selected, viewOffsetWells });

    let buttonText = 'Add as Treatment Well';
    let action = (data.bottomHoleLat && data.bottomHoleLng && data.polylinePath) ?
      handleSelectTreatmentWell
      : null;
    if (viewOffsetWells && selected !== null && selected !== undefined) {
      action = handleSelectOffsetWell;
      buttonText = 'Add as Offset Well';
    }
    if (data?.markerType === 'offsetWell') {
      action = handleDeleteOffsetWell;
      buttonText = 'Remove Offset Well';
    }
    setRenderInfoWindow(
      <InfoWindow
        position={{ lat: data.lat, lng: data.lng }}
        onCloseClick={() => {
          handleCloseInfoWindow();
        }}
        options={{ disableAutoPan: true }}
      //options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
      >
        {InfoWindowContent(data, { action, buttonText })}
      </InfoWindow>
    );
  };

  const handleCloseInfoWindow = () => {
    setRenderInfoWindow(null);
  };

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        onIdle={handleOnIdle}
        options={{ ...mapOptions, mapTypeId: mapType }}
      >
        {renderDirectionalSurveys()}
        {renderCartofactWells()}
        {renderSelectedTreatmentWells()}
        {renderSelectedOffsetWells()}
        {renderInfoWindow}
      </GoogleMap>
      <Backdrop
        open={((currentZoom < MIN_WELL_ZOOM) && zoomOverlay)}
        sx={(theme) => ({
          zIndex: theme.zIndex.modal + 1,
          pointerEvents: 'none',          // let map receive scroll/drag/zoom
          backgroundColor: 'rgba(0,0,0,0.08)', // optional subtle dim
        })}
      >
          <div
            style={{
              pointerEvents: 'none',         // keep everything non-interactive
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              borderRadius: 12,
            background: 'rgba(0,0,0,0.75)',
              color: '#fff',
            }}
          >
            <h2>Zoom in to load wells</h2>
          { /*<CircularProgress color="inherit" />*/}
          </div>
      </Backdrop>
    </LoadScript>
  )
}

export default Map;